import React from 'react'

import { ExternalLink } from 'lucide-react'

import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

// PRO Organization interface
interface Organization {
	name: string
	country: string
	website: string | null
}

// Complete list of PRO organizations by country
const ORGANIZATIONS: Organization[] = [
	// Argentina (AR)
	{
		name: 'SADAIC',
		country: 'AR',
		website:
			'https://www.sadaic.org.ar/shared/cdrw/pdf-Aranceles-Usuarios-Generales_Tabla%20al_01_03-2024.pdf',
	},
	{
		name: 'AADI CAPIF',
		country: 'AR',
		website: 'https://www.aadi-capif.org.ar/Home/Welcome',
	},

	// México (MX)
	{
		name: 'SOMEXFON',
		country: 'MX',
		website: 'https://somexfon.com/tarifario/',
	},
	{
		name: 'SACM',
		country: 'MX',
		website:
			'https://www.sacm.org.mx//repositorio/Cuotas/pdf/Tarifario2017.pdf#viewer.action=download',
	},
	{ name: 'ANDI', country: 'MX', website: 'https://www.andi.org.mx/' },

	// España (ES)
	{
		name: 'SGAE',
		country: 'ES',
		website:
			'https://documentos-sgae.s3.eu-west-1.amazonaws.com/SGAE-tarifas-2024_v4.pdf#page=27',
	},
	{
		name: 'AGEDI / AIE',
		country: 'ES',
		website: 'https://www.agedi-aie.es/tarifas/ejecucion-publica',
	},

	// Colombia (CO)
	{
		name: 'OSA (SAYCO ACINPRO)',
		country: 'CO',
		website: 'https://www.osa.org.co/simulador-de-tarifas',
	},

	// Chile (CL)
	{
		name: 'PROFOVI',
		country: 'CL',
		website: 'https://profovi.cl/tarifas.html',
	},
	{
		name: 'SCD',
		country: 'CL',
		website: 'https://www.scd.cl/yo-utilizo-musica/tarifas/',
	},

	// Panamá (PA)
	{
		name: 'PRODUCE',
		country: 'PA',
		website: 'https://www.producepanama.org/tarifas-oficiales/#single/0',
	},
	{
		name: 'SPAC',
		country: 'PA',
		website: 'https://spac.org.pa/wp-content/uploads/2023/12/SPAC-tarifas.pdf',
	},

	// Perú (PE)
	{
		name: 'UNIMPRO',
		country: 'PE',
		website:
			'https://unimpro.org/wp-content/uploads/2024/01/02Tarifas_de_Establecimientos.pdf',
	},
	{
		name: 'APDAYC',
		country: 'PE',
		website: 'https://www.apdayc.org.pe/Tarifario/2024/Anexo%201%202024.pdf',
	},

	// Ecuador (EC)
	{
		name: 'SOPROFON',
		country: 'EC',
		website: 'https://soprofon.ec/calculadora/',
	},
	{
		name: 'SAYCE',
		country: 'EC',
		website: 'https://sayce.com.ec/tarifas-sayce/por-comunicacion-publica/',
	},

	// Bolivia (BO)
	{
		name: 'SOBODAYCOM',
		country: 'BO',
		website:
			'https://www.sobodaycom.org/wp-content/uploads/2023/02/Tarifario-SOBODAYCOM.pdf',
	},
	{ name: 'ABAIEM', country: 'BO', website: null },
	{ name: 'ASBOPROFON', country: 'BO', website: null },

	// Costa Rica (CR)
	{ name: 'ACAM', country: 'CR', website: 'https://acam.cr/tarifario/' },
	{ name: 'AIE', country: 'CR', website: 'https://aiecostarica.com/' },
	{
		name: 'FONOTICA',
		country: 'CR',
		website:
			'https://img1.wsimg.com/blobby/go/93952867-7f7c-4d7c-812c-02e39b8ba08f/downloads/TARIFAS%20FONOTICA%202020.pdf?ver=1721858557215',
	},

	// República Dominicana (DO)
	{ name: 'SGACEDOM', country: 'DO', website: 'https://sgacedom.org/' },
	{
		name: 'SODAIE',
		country: 'DO',
		website: 'https://67m.9b1.myftpupload.com/formulario_licencia_usuarios/',
	},
	{
		name: 'SODINPRO',
		country: 'DO',
		website: 'https://sodinpro.org/solicitud-de-licencia/',
	},

	// El Salvador (SV)
	{ name: 'SACIM', country: 'SV', website: 'https://sacim.org/www/' },
	{ name: 'ASAP EGC', country: 'SV', website: 'https://www.asap-egc.com/' },

	// Guatemala (GT)
	{
		name: 'AEI-GUATEMALA',
		country: 'GT',
		website:
			'https://irp.cdn-website.com/5c5e0c09/files/uploaded/Tarifario%20AEI-Guatemala%20SGS%202021.pdf',
	},
	{ name: 'MUSICARTES', country: 'GT', website: 'https://musicartes.org/' },
	{ name: 'AGINPRO', country: 'GT', website: 'https://www.aginpro.org/SITE/#' },

	// Honduras (HN)
	{
		name: 'AACIMH',
		country: 'HN',
		website: 'https://aacimh.org.hn/licencias/',
	},

	// Nicaragua (NI)
	{ name: 'NICAUTOR', country: 'NI', website: null },

	// Uruguay (UY)
	{
		name: 'AGADU',
		country: 'UY',
		website: 'https://www.agadu.org/documentos/Aranceles_pequeno_derecho.pdf',
	},
	{ name: 'SUDEI', country: 'UY', website: 'https://www.sudei.org.uy/' },
	{ name: 'CUD', country: 'UY', website: 'https://cudisco.org/' },

	// Venezuela (VE)
	{ name: 'SACVEN', country: 'VE', website: 'https://sacven.org/wp/' },
	{ name: 'AVINPRO', country: 'VE', website: 'https://www.avinpro.com/' },
]

// Country name mapping for display
const COUNTRY_NAMES: Record<string, string> = {
	AR: 'Argentina',
	MX: 'Mexico',
	ES: 'Spain',
	CO: 'Colombia',
	CL: 'Chile',
	PA: 'Panama',
	PE: 'Peru',
	EC: 'Ecuador',
	BO: 'Bolivia',
	CR: 'Costa Rica',
	DO: 'Dominican Republic',
	SV: 'El Salvador',
	GT: 'Guatemala',
	HN: 'Honduras',
	NI: 'Nicaragua',
	UY: 'Uruguay',
	VE: 'Venezuela',
}

const MusicLegalCompliance: React.FC = () => {
	const { t } = useTranslation('common')
	const { features } = usePermission()
	const currentAccount = useCurrentAccount()

	// Check if the account has Soundreef
	const isSoundreef = currentAccount?.royalty?.name === 'Soundreef'

	// Get organizations for the account's country
	const accountCountry = currentAccount?.country
	const organizations = ORGANIZATIONS.filter(
		(org) => org.country === accountCountry
	)

	// Get the country display name
	const countryName = accountCountry
		? COUNTRY_NAMES[accountCountry] || accountCountry
		: ''

	// Check visibility conditions
	const isEnabled = features.account.musicLegalCompliance

	// Don't render if disabled or no organizations for the country (and not Soundreef)
	if (!isEnabled || (!isSoundreef && organizations.length === 0)) {
		return null
	}

	const companyName = t('app.name')

	// Helper to get domain from URL for display
	const getDomainFromUrl = (url: string): string => {
		try {
			const urlObj = new URL(url)
			return urlObj.hostname.replace('www.', '')
		} catch {
			return url
		}
	}

	return (
		<div className="space-y-6">
			<h2 className="text-lg font-semibold">
				{t('account.music_legal_compliance.title')}
			</h2>

			{isSoundreef ? (
				// Soundreef variant - only show informational message
				<p className="text-sm text-muted-foreground">
					{t('account.music_legal_compliance.soundreef', {
						company: companyName,
					})}
				</p>
			) : (
				// Non-Soundreef variant - show description + organizations list
				<>
					<p className="text-sm text-muted-foreground">
						{t('account.music_legal_compliance.description', {
							company: companyName,
						})}
					</p>

					{/* Organizations list */}
					<div className="space-y-3">
						<h3 className="text-sm font-medium text-muted-foreground">
							{t('account.music_legal_compliance.pro', {
								country: countryName,
							})}
						</h3>

						<div className="space-y-2">
							{organizations.map((org) => (
								<div
									key={org.name}
									className="flex items-center p-4 rounded-lg border border-border bg-card"
								>
									<div className="flex flex-col gap-1">
										<span className="font-medium text-foreground">
											{org.name}
										</span>
										{org.website && (
											<a
												href={org.website}
												target="_blank"
												rel="noopener noreferrer"
												className="text-sm text-primary hover:underline flex items-center gap-1"
											>
												{getDomainFromUrl(org.website)}
												<ExternalLink className="h-3 w-3 shrink-0" />
											</a>
										)}
									</div>
								</div>
							))}
						</div>
					</div>
				</>
			)}
		</div>
	)
}

export default MusicLegalCompliance
